import React from 'react';
import PropTypes from 'prop-types';
import { Button, Card, Col, Row } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileInvoiceDollar, faSync } from '@fortawesome/free-solid-svg-icons';
import Background from 'components/common/Background';
import Pagination from 'components/common/Pagination';
import SoftBadge from 'components/common/SoftBadge';
import corner6 from 'assets/img/illustrations/corner-6.png';

export const BillingHeader = ({ title, subtitle, onRefresh, loading }) => (
  <Card.Header className="position-relative px-4 py-3">
    <Background
      image={corner6}
      className="bg-card rounded-top"
      style={{ pointerEvents: 'none' }}
    />
    <Row className="align-items-center g-2 position-relative">
      <Col>
        <h3 className="mb-1">{title}</h3>
        <p className="text-600 fs--1 mb-0">{subtitle}</p>
      </Col>
      {onRefresh && (
        <Col xs="auto">
          <Button
            variant="falcon-default"
            size="sm"
            onClick={onRefresh}
            disabled={loading}
          >
            <FontAwesomeIcon icon={faSync} spin={loading} className="me-2" />
            Refresh
          </Button>
        </Col>
      )}
    </Row>
  </Card.Header>
);

export const BillingStatCard = ({ label, value, icon, tone, children }) => (
  <Card className="h-100 shadow-none border">
    <Card.Body className="d-flex flex-column p-3">
      <div className="d-flex align-items-center justify-content-between mb-2">
        <h6 className="text-600 fs--1 fw-semi-bold mb-0">{label}</h6>
        {icon && (
          <span
            className={`d-inline-flex align-items-center justify-content-center rounded-circle bg-soft-${
              tone || 'primary'
            } text-${tone || 'primary'}`}
            style={{ width: 32, height: 32 }}
            aria-hidden="true"
          >
            <FontAwesomeIcon icon={icon} size="sm" />
          </span>
        )}
      </div>
      <div className="fs-3 fw-bold text-900 lh-1">{value}</div>
      {children && <div className="border-top mt-3 pt-2 fs--1">{children}</div>}
    </Card.Body>
  </Card>
);

export const BillingStatRow = ({ label, value }) => (
  <div className="d-flex justify-content-between gap-2 py-1">
    <span className="text-600">{label}</span>
    <span className="fw-semi-bold text-900">{value}</span>
  </div>
);

BillingStatCard.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.node.isRequired,
  icon: PropTypes.object,
  tone: PropTypes.string,
  children: PropTypes.node
};
BillingStatRow.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.node.isRequired
};

export const BillingEmptyState = ({ children }) => (
  <div className="text-center py-5 text-600">
    <FontAwesomeIcon
      icon={faFileInvoiceDollar}
      size="3x"
      className="text-400"
    />
    <p className="mt-3 mb-0 fs--1">{children}</p>
  </div>
);

export const BillingEmptyRow = ({ colSpan, children }) => (
  <tr>
    <td colSpan={colSpan}>
      <BillingEmptyState>{children}</BillingEmptyState>
    </td>
  </tr>
);

BillingEmptyRow.propTypes = {
  colSpan: PropTypes.number.isRequired,
  children: PropTypes.node.isRequired
};

export const BillingStatus = ({ children }) => {
  const status = String(children ?? '');
  const bg = ['Paid', 'Active', 'Completed', 'Closed', 'Succeeded'].includes(
    status
  )
    ? 'success'
    : ['Restricted', 'Failed'].includes(status)
    ? 'danger'
    : ['Open', 'Pending', 'RequiresPricingReview'].includes(status)
    ? 'warning'
    : 'secondary';
  return (
    <SoftBadge bg={bg} pill>
      {children}
    </SoftBadge>
  );
};

export const BillingPagination = ({
  page,
  pageSize,
  onPageChange,
  onPageSizeChange,
  onRefresh,
  data,
  loading,
  error
}) => (
  <div className="mb-2">
    <Pagination
      className="flex-wrap gap-2"
      pageIndex={page}
      pageSize={pageSize}
      totalRecords={data?.TotalRecords ?? 0}
      currentCount={data?.Objects?.length ?? 0}
      onPageChange={onPageChange}
      onPageSizeChange={onPageSizeChange}
      endOfResults={data?.EndOfResults !== false}
      disabled={Boolean(loading || error)}
      pageSizeOptions={[10, 25, 50]}
      onRefresh={onRefresh}
      loading={loading}
    />
  </div>
);

BillingHeader.propTypes = {
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.string,
  onRefresh: PropTypes.func,
  loading: PropTypes.bool
};
BillingEmptyState.propTypes = { children: PropTypes.node };
BillingStatus.propTypes = { children: PropTypes.node };
BillingPagination.propTypes = {
  page: PropTypes.number.isRequired,
  pageSize: PropTypes.number.isRequired,
  onPageChange: PropTypes.func.isRequired,
  onPageSizeChange: PropTypes.func.isRequired,
  onRefresh: PropTypes.func,
  data: PropTypes.object,
  loading: PropTypes.bool,
  error: PropTypes.any
};
